import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type {
  CallFilters,
  UpdateCallInput,
  LinkCallToCrmInput,
  CallAnalyticsQuery,
} from '@/shared/schemas/call.schema'
import { buildQueryString, fetchJson, fetchMutation } from '@/client/lib/api-helpers'

const API_BASE = '/api/calls'

// Types based on our database schema
export interface CallMessage {
  id: string
  callId: string
  role: 'agent' | 'user' | 'system'
  message: string
  timeInCallSecs: number | null
  sequenceNumber: number
  toolCalls: string | null
  toolResults: string | null
  turnMetrics: string | null
  llmUsage: string | null
  interrupted: boolean | null
  sourceMedium: string | null
  originalMessage: string | null
  ragRetrievalInfo: string | null
  createdAt: string
}

export interface CallAnalysis {
  id: string
  callId: string
  source: 'eleven_labs' | 'workers_ai' | 'manual'
  evaluationCriteriaResults: string | null
  dataCollectionResults: string | null
  callSuccessful: boolean | null
  summary: string | null
  topics: string | null
  actionItems: string | null
  sentiment: 'positive' | 'neutral' | 'negative' | 'mixed' | null
  sentimentScore: number | null
  outcomeType: string | null
  outcomeNotes: string | null
  extractedName: string | null
  extractedEmail: string | null
  extractedPhone: string | null
  extractedCompany: string | null
  extractedData: string | null
  processingStatus: 'pending' | 'processing' | 'completed' | 'failed'
  processingError: string | null
  createdAt: string
  updatedAt: string
}

export interface Call {
  id: string
  userId: string | null
  conversationId: string
  agentId: string
  agentName: string | null
  callType: 'voice_agent' | 'phone_inbound' | 'phone_outbound' | 'web_call'
  source: 'eleven_labs' | 'twilio' | 'internal'
  status: 'initiated' | 'in_progress' | 'completed' | 'failed' | 'no_answer' | 'busy'
  terminationReason: string | null
  startTime: string | null
  endTime: string | null
  durationSeconds: number | null
  cost: number | null
  costCurrency: string | null
  callSuccessful: boolean | null
  transcriptSummary: string | null
  feedbackRating: number | null
  hasAudio: boolean | null
  hasUserAudio: boolean | null
  hasResponseAudio: boolean | null
  callerPhone: string | null
  callerName: string | null
  callerEmail: string | null
  enquiryId: string | null
  contactId: string | null
  companyId: string | null
  recordingPath: string | null
  transcriptPath: string | null
  metadata: string | null
  createdAt: string
  updatedAt: string
  // Relations (when included)
  messages?: CallMessage[]
  analyses?: CallAnalysis[]
  enquiry?: { id: string; name: string; email: string; status: string } | null
  contact?: { id: string; firstName: string; lastName: string; email: string } | null
  company?: { id: string; name: string } | null
}

export interface CallsResponse {
  calls: Call[]
  pagination: {
    total: number
    limit: number
    offset: number
    hasMore: boolean
  }
}

export interface CallAgent {
  agentId: string
  agentName: string | null
  callCount: number
  lastCallAt: string
}

export interface CallAnalyticsSummary {
  totalCalls: number
  completedCalls: number
  failedCalls: number
  avgDurationSeconds: number
  totalCost: number
  successRate: number
  byOutcome: Record<string, number>
  bySentiment: Record<string, number>
}

// Query keys factory
export const CALLS_KEYS = {
  all: ['calls'] as const,
  lists: () => [...CALLS_KEYS.all, 'list'] as const,
  list: (params?: CallFilters) => [...CALLS_KEYS.lists(), params] as const,
  details: () => [...CALLS_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...CALLS_KEYS.details(), id] as const,
  agents: () => [...CALLS_KEYS.all, 'agents'] as const,
  analytics: (params?: CallAnalyticsQuery) => [...CALLS_KEYS.all, 'analytics', params] as const,
}

// Fetch calls with pagination and filtering
export function useCalls(params?: Partial<CallFilters>) {
  return useQuery({
    queryKey: CALLS_KEYS.list(params as CallFilters),
    queryFn: async (): Promise<CallsResponse> => {
      return fetchJson<CallsResponse>(`${API_BASE}${buildQueryString(params)}`)
    },
  })
}

// Fetch single call with full details
export function useCall(id: string | undefined) {
  return useQuery({
    queryKey: CALLS_KEYS.detail(id!),
    queryFn: async (): Promise<Call> => {
      const data = await fetchJson<{ call: Call }>(`${API_BASE}/${id}`)
      return data.call
    },
    enabled: !!id,
  })
}

// Update call
export function useUpdateCall() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateCallInput }): Promise<Call> => {
      const result = await fetchMutation<{ call: Call }>(`${API_BASE}/${id}`, 'PATCH', data)
      return result.call
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: CALLS_KEYS.lists() })
      queryClient.invalidateQueries({ queryKey: CALLS_KEYS.detail(variables.id) })
    },
  })
}

// Link call to CRM entities
export function useLinkCallToCrm() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: Omit<LinkCallToCrmInput, 'callId'>
    }): Promise<Call> => {
      const result = await fetchMutation<{ call: Call }>(`${API_BASE}/${id}/link`, 'POST', data)
      return result.call
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: CALLS_KEYS.lists() })
      queryClient.invalidateQueries({ queryKey: CALLS_KEYS.detail(variables.id) })
    },
  })
}

// Trigger AI reprocessing
export function useReprocessCall() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string): Promise<{ message: string; analysisId: string }> => {
      return fetchMutation<{ message: string; analysisId: string }>(`${API_BASE}/${id}/reprocess`, 'POST')
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: CALLS_KEYS.detail(id) })
    },
  })
}

// Delete call
export function useDeleteCall() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await fetchMutation<{ success: boolean }>(`${API_BASE}/${id}`, 'DELETE')
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: CALLS_KEYS.lists() })
      queryClient.removeQueries({ queryKey: CALLS_KEYS.detail(id) })
    },
  })
}

// Fetch agents list
export function useCallAgents() {
  return useQuery({
    queryKey: CALLS_KEYS.agents(),
    queryFn: async (): Promise<CallAgent[]> => {
      const data = await fetchJson<{ agents: CallAgent[] }>(`${API_BASE}/agents/list`)
      return data.agents
    },
  })
}

// Fetch analytics
export function useCallAnalytics(params: CallAnalyticsQuery) {
  return useQuery({
    queryKey: CALLS_KEYS.analytics(params),
    queryFn: async (): Promise<CallAnalyticsSummary> => {
      return fetchJson<CallAnalyticsSummary>(`${API_BASE}/analytics/summary${buildQueryString(params)}`)
    },
    enabled: !!params.startDate && !!params.endDate,
  })
}
